import http from '../http-common';
import { withRunParam } from './analysisRunOptions';

class DetectionFeedbackService {
  /**
   * @param {{
   *   source_type: 'interview'|'manual_analysis',
   *   source_id: number,
   *   idea_id?: string|null,
   *   target_type: 'idea_verdict'|'idea_alert'|'gesture',
   *   target_ref: string,
   *   gesture_name?: string|null,
   *   start_ms?: number|null,
   *   end_ms?: number|null,
   *   verdict: 'correct'|'incorrect'|'missing',
   *   error_type?: 'over_detected'|'under_detected'|'wrong_label'|'wrong_timing'|'other'|null,
   *   cause?: 'technical'|'methodological'|null,
   *   expected_timestamp_ms?: number|null,
   *   note?: string|null,
   * }} payload
   */
  create(payload) {
    return http.post('/detection-feedback', payload);
  }

  /**
   * Feedback rows of one source, strictly for one run (M2 contract 5).
   * @param {'interview'|'manual_analysis'} sourceType
   * @param {number} sourceId
   * @param {string|null} run - Optional run id (`?run=`); omitted -> active run
   */
  list(sourceType, sourceId, run = null) {
    return http.get(`/detection-feedback/${sourceType}/${sourceId}`, {
      params: withRunParam({}, run),
    });
  }

  update(id, patch) {
    return http.patch(`/detection-feedback/${id}`, patch);
  }

  remove(id) {
    return http.delete(`/detection-feedback/${id}`);
  }

  /**
   * target_ref (alert_id) is a fresh uuid per run; gesture_name + start/end ms are the
   * durable coordinate that survives a re-analysis and makes the row usable for
   * per-gesture metrics.
   */
  submitAlertFeedback({
    sourceType,
    sourceId,
    ideaId,
    alertId,
    gestureName = null,
    startMs = null,
    endMs = null,
    verdict,
    errorType = null,
    cause = null,
    note = null,
  }) {
    return this.create({
      source_type: sourceType,
      source_id: sourceId,
      idea_id: ideaId ?? null,
      target_type: 'idea_alert',
      target_ref: alertId,
      gesture_name: gestureName,
      start_ms: startMs,
      end_ms: endMs,
      verdict,
      error_type: errorType,
      cause,
      note,
    });
  }

  submitGestureFeedback({
    sourceType,
    sourceId,
    gestureId,
    gestureName = null,
    startMs = null,
    endMs = null,
    verdict,
    errorType = null,
    cause = null,
    note = null,
  }) {
    return this.create({
      source_type: sourceType,
      source_id: sourceId,
      idea_id: null,
      target_type: 'gesture',
      target_ref: gestureId,
      // Optional: enables per-gesture-name precision/recall on the QA Review dashboard.
      gesture_name: gestureName,
      start_ms: startMs,
      end_ms: endMs,
      verdict,
      error_type: errorType,
      cause,
      note,
    });
  }

  submitMissingAlert({ sourceType, sourceId, ideaId, expectedTimestampMs, cause = null, note = null }) {
    return this.create({
      source_type: sourceType,
      source_id: sourceId,
      idea_id: ideaId ?? null,
      target_type: 'idea_alert',
      target_ref: `missing:${expectedTimestampMs}`,
      verdict: 'missing',
      error_type: 'under_detected',
      cause,
      expected_timestamp_ms: expectedTimestampMs,
      note,
    });
  }

  /**
   * Missing GESTURE (not idea alert): counts as FN in gesture metrics. gestureName is
   * what makes per-gesture recall possible; the point in time is stored as a
   * degenerate [start, end] so it re-matches by overlap like any other label.
   */
  submitMissingGesture({ sourceType, sourceId, gestureName = null, expectedTimestampMs, cause = null, note = null }) {
    return this.create({
      source_type: sourceType,
      source_id: sourceId,
      idea_id: null,
      target_type: 'gesture',
      target_ref: `missing:${expectedTimestampMs}`,
      gesture_name: gestureName,
      start_ms: expectedTimestampMs,
      end_ms: expectedTimestampMs,
      verdict: 'missing',
      error_type: 'under_detected',
      cause,
      expected_timestamp_ms: expectedTimestampMs,
      note,
    });
  }

  /**
   * Gesture detection metrics for QA Review, grouped by gesture_name.
   * Response (under `data`): { global, bySource } where global = { tp, fp, fn, total,
   * precision, recall, f1, byGesture: [{ gesture_name, label, tp, fp, fn, precision,
   * recall, f1, total, caveats }], retired: { label: 'retired', gesture_names, ...counts },
   * unattributed, caveats: string[] } and bySource = [{ source_type, source_id,
   * video_name, ...same as global }].
   */
  getGestureMetrics(filters = {}) {
    const params = {};
    if (filters.sourceType) params.sourceType = filters.sourceType;
    if (filters.sourceId) params.sourceId = filters.sourceId;
    return http.get('/detection-feedback/gesture-metrics', { params });
  }

  /**
   * Re-matches the stored gesture labels of one source against the episodes of its
   * current run (time overlap, like video_incoherence), so the analyst can see which labels
   * still land on a detection after a re-analysis.
   * Response (under `data`, GestureRematchReport): { source_type, source_id,
   * current_run: { interview_analysis_id, analysis_config_id, analysis_config_version,
   * analysis_config_hash } | null, stamped_runs: [{ interview_analysis_id,
   * analysis_config_hash, labels }], same_config_hash: boolean | null,
   * rematch: { labels, matched, lost, missing_now_detected, missing_still_missing,
   * no_time_coordinate, unattributed, byGesture: [{ gesture_name, label, labels, matched,
   * lost, missing_now_detected, missing_still_missing, no_time_coordinate,
   * current_episodes }] }, caveats: string[] }.
   */
  getGestureRematch({ sourceType, sourceId }) {
    return http.get('/detection-feedback/gesture-metrics/rematch', {
      params: { sourceType, sourceId },
    });
  }

  submitIdeaVerdictFeedback({ sourceType, sourceId, ideaId, verdict, cause = null, note = null }) {
    return this.create({
      source_type: sourceType,
      source_id: sourceId,
      idea_id: ideaId ?? null,
      target_type: 'idea_verdict',
      target_ref: ideaId,
      verdict,
      error_type: null,
      cause,
      note,
    });
  }
}

export default new DetectionFeedbackService();
